/**
 * useScheduleDraftFlightMutations — ScheduleDraft 航班级写操作 hook 集合。
 *
 * 本模块封装新增、更新、删除与来源导入航班的 React Query mutation。它把
 * payload builder、Feature data service 与缓存失效策略绑定在一起，供编辑器
 * 编排层以语义方法调用。
 *
 * 设计文档 → docs/10_core_contracts/10_data_model.md
 * 骨架关联 → services/scheduleDraftDataService.js (HTTP 边界), services/scheduleDraftPayloadBuilders.js (payload), hooks/useScheduleDraftMutationHelper.js (缓存失效)
 */

import { useMutation } from "react-query";
import { scheduleDraftQueryKeys } from "./queryKeys";
import { useScheduleDraftMutationHelper } from "./useScheduleDraftMutationHelper";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";
import { buildScheduleDraftFlightPayload, buildScheduleDraftSourceFlightsPayload, buildScheduleDraftTableImportPayload } from "../services/scheduleDraftPayloadBuilders";

export function useAddScheduleDraftFlight(draftId, currentEditingTrackId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
        ...options,
    });

    return useMutation(
        async (values) => {
            const trackId = values?.track_id || values?.trackId || currentEditingTrackId;
            return scheduleDraftDataService.addFlightToTrack(draftId, trackId, buildScheduleDraftFlightPayload(values));
        },
        buildMutationOptions(),
    );
}

export function useUpdateScheduleDraftFlight(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
        ...options,
    });

    return useMutation(
        async ({ flightId, values }) => {
            return scheduleDraftDataService.updateFlight(draftId, flightId, buildScheduleDraftFlightPayload(values));
        },
        buildMutationOptions(),
    );
}

export function useDeleteScheduleDraftFlight(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
        ...options,
    });

    return useMutation(
        (flightId) => scheduleDraftDataService.deleteFlight(draftId, flightId),
        buildMutationOptions(),
    );
}

export function useMoveScheduleDraftFlight(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
        ...options,
    });

    return useMutation(
        ({ flightId, values }) => scheduleDraftDataService.moveFlight(draftId, flightId, values),
        buildMutationOptions(),
    );
}

export function useImportScheduleDraftSourceFlights(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
        ...options,
    });

    return useMutation(
        (values) => scheduleDraftDataService.importSourceFlights(draftId, buildScheduleDraftSourceFlightsPayload(values)),
        buildMutationOptions(),
    );
}

export function useImportScheduleDraftTableFlights(draftId, options = {}) {
    const { buildMutationOptions } = useScheduleDraftMutationHelper({
        invalidateQueryKeys: [scheduleDraftQueryKeys.detail(draftId), scheduleDraftQueryKeys.lists()],
        ...options,
    });

    return useMutation(
        ({ values, tableModel }) => scheduleDraftDataService.importTableFlights(draftId, buildScheduleDraftTableImportPayload(values, tableModel)),
        buildMutationOptions(),
    );
}

const scheduleDraftFlightMutations = {
    useAddScheduleDraftFlight,
    useUpdateScheduleDraftFlight,
    useDeleteScheduleDraftFlight,
    useMoveScheduleDraftFlight,
    useImportScheduleDraftSourceFlights,
    useImportScheduleDraftTableFlights,
};

export default scheduleDraftFlightMutations;
